import { Box } from "@mui/material";
import { DateTime } from "luxon";
import { type FC, type FormEvent, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useAuth } from "../../contexts/AuthProvider";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useUserShifts } from "../../hooks/useUserShifts";
import type { Message, NurseInShift } from "../../utils/models";
import { Chat } from "./Chat";

const DEFAULT_MESSAGES: Message[] = [
	{
		id: uuidv4(),
		text: "Hi! Annie here, what can i do for you?",
		sender: "server",
		timestamp: new Date().toISOString(),
	},
];

// Helper function to detect schedule-related queries
// Examples that trigger this:
// - "what's my schedule this week?"
// - "when am I working?"
const detectScheduleIntent = (message: string): boolean => {
	const lowercaseMsg = message.toLowerCase();
	const scheduleKeywords = ["schedule", "shifts", "working", "work"];
	const timeKeywords = ["week"];

	return scheduleKeywords.some((keyword) => lowercaseMsg.includes(keyword)) && timeKeywords.some((keyword) => lowercaseMsg.includes(keyword));
};

// Helper function to detect next shift queries
// Examples that trigger this:
// - "when is my next shift?"
// - "what's my next shift"
// - "when do I work next?"
// - "next shift"
// - "when am I working next"
const detectNextShiftIntent = (message: string): boolean => {
	const lowercaseMsg = message.toLowerCase();
	return (
		(lowercaseMsg.includes("next") && (lowercaseMsg.includes("shift") || lowercaseMsg.includes("work"))) ||
		(lowercaseMsg.includes("when") && (lowercaseMsg.includes("shift") || lowercaseMsg.includes("work")))
	);
};

// Helper function to detect sick call-out intent
// Examples that trigger this:
// - "I'm sick and can't come in"
// - "I'm not feeling well"
// - "I need to call out"
// - "I can't make it to my shift today"
// - "I'm ill and won't be able to work"
// - "calling in sick"
const detectCallOutIntent = (message: string): boolean => {
	const lowercaseMsg = message.toLowerCase();
	const sickPatterns = ["sick", "ill", "not feeling well", "unwell"];
	const callOutPatterns = ["can't come in", "can't make it", "won't be able", "call out", "calling out", "calling in"];

	return sickPatterns.some((pattern) => lowercaseMsg.includes(pattern)) || callOutPatterns.some((pattern) => lowercaseMsg.includes(pattern));
};

// Helper function to detect call-out process questions
// Examples that trigger this:
// - "What do I need to do to call out?"
// - "How do I call in sick?"
// - "What's the sick day procedure?"
// - "I need to call out, what should I do?"
const detectCallOutQuestionIntent = (message: string): boolean => {
	const lowercaseMsg = message.toLowerCase();
	const questionPatterns = [
		"what do i need to do",
		"what do i have to do",
		"how do i call out",
		"how to call out",
		"call out process",
		"sick day procedure",
		"what should i do",
	];

	return questionPatterns.some((pattern) => lowercaseMsg.includes(pattern));
};

// Helper function to detect pickup shift intent
// Examples that trigger this:
// - "I can pick up an extra shift this Monday"
// - "Any extra shifts available?"
// - "Looking to pick up shifts early next week"
// - "Anything open?"
// - "Can I pick up a shift?"
const detectPickupShiftIntent = (message: string): boolean => {
	const lowercaseMsg = message.toLowerCase();
	const pickupPatterns = ["pick up", "pickup", "extra shift", "available", "anything open", "open shift"];

	return pickupPatterns.some((pattern) => lowercaseMsg.includes(pattern));
};

// Helper function to detect bad day at work intent
// Examples that trigger this:
// - "I'm having a bad day at work"
// - "Today was terrible"
// - "This is a rough day"
// - "Having a difficult time at work"
// - "Work is really stressful today"
// - "I'm struggling today"
const detectBadDayIntent = (message: string): boolean => {
	const lowercaseMsg = message.toLowerCase();
	const badDayPatterns = [
		"bad day",
		"terrible day",
		"awful day",
		"horrible day",
		"rough day",
		"difficult day",
		"hard day",
		"stressful day",
		"frustrating day",
		"worst day",
		"tough day",
		"struggling",
		"having a hard time",
		"difficult time",
		"rough time",
		"bad day at work",
		"terrible day at work",
		"rough day at work",
		"difficult day at work",
		"hard day at work",
		"stressful day at work",
		"frustrating day at work",
		"worst day at work",
		"tough day at work",
	];

	return badDayPatterns.some((pattern) => lowercaseMsg.includes(pattern));
};

// Helper to determine if message mentions a specific day
const mentionsSpecificDay = (message: string): boolean => {
	const days = [
		"monday",
		"mon",
		"tuesday",
		"tue",
		"wednesday",
		"wed",
		"thursday",
		"thu",
		"friday",
		"fri",
		"saturday",
		"sat",
		"sunday",
		"sun",
		"today",
		"tomorrow",
	];
	const lowercaseMsg = message.toLowerCase();
	return days.some((day) => lowercaseMsg.includes(day));
};

// Helper to extract the day from the message
const extractDay = (message: string): string => {
	const lowercaseMsg = message.toLowerCase();
	const now = DateTime.now();

	// Check for "today" or "tomorrow"
	if (lowercaseMsg.includes("today")) {
		return now.toFormat("cccc");
	}
	if (lowercaseMsg.includes("tomorrow")) {
		return now.plus({ day: 1 }).toFormat("cccc");
	}

	// Check for day names
	const dayMap: Record<string, string> = {
		monday: "Monday",
		mon: "Monday",
		tuesday: "Tuesday",
		tue: "Tuesday",
		wednesday: "Wednesday",
		wed: "Wednesday",
		thursday: "Thursday",
		thu: "Thursday",
		friday: "Friday",
		fri: "Friday",
		saturday: "Saturday",
		sat: "Saturday",
		sunday: "Sunday",
		sun: "Sunday",
	};

	for (const [key, value] of Object.entries(dayMap)) {
		if (lowercaseMsg.includes(key)) {
			return value;
		}
	}

	return "Monday"; // fallback
};

// Helper to determine if it's a general availability question
const isGeneralAvailabilityQuestion = (message: string): boolean => {
	const generalPatterns = ["any", "available", "what's", "whats", "early next week"];
	const lowercaseMsg = message.toLowerCase();
	return generalPatterns.some((pattern) => lowercaseMsg.includes(pattern));
};

// Find the next upcoming shift
const findNextShift = (shifts: Record<string, NurseInShift[]> | undefined): { shift: NurseInShift; date: DateTime } | null => {
	if (!shifts) return null;

	const now = DateTime.now();
	const sortedDates = Object.keys(shifts).sort();

	for (const dateKey of sortedDates) {
		const shiftDate = DateTime.fromFormat(dateKey, "yyyy-MM-dd");
		const dayShifts = shifts[dateKey];

		if (dayShifts && dayShifts.length > 0) {
			// Check if this shift is in the future
			const shift = dayShifts[0];
			if (shift.startTime > now) {
				return { shift, date: shiftDate };
			}
		}
	}

	return null;
};

// Format call-out response (static messages, no shift data check)
const formatCallOutResponse = (isQuestion: boolean): string => {
	if (isQuestion) {
		// Scenario B: Question about process
		return `Go rest! That's the only task you have today. I've taken care of finding coverage and informed your manager. See you soon`;
	}
	// Scenario A: Direct call-out
	return `Oh no, I'm sorry to hear that. I've taken care of finding coverage and informed your manager. See you soon!`;
};

// Format pickup shift response (static messages based on message pattern)
const formatPickupShiftResponse = (message: string): string => {
	if (mentionsSpecificDay(message)) {
		// Scenario A: Mentions specific day (e.g., "Monday", "today", "tomorrow")
		const mentionedDay = extractDay(message);
		// Get the next day of the week
		const dayIndex = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"].indexOf(mentionedDay);
		const nextDay = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][(dayIndex + 1) % 7];
		return `Thanks! ${mentionedDay} is already fully staffed, but we do need coverage on ${nextDay} 3pm–11pm.
Would you like me to assign you to that shift?`;
	}
	if (isGeneralAvailabilityQuestion(message)) {
		// Scenario B: General availability question
		return `We're covered on Monday, but Tuesday has an open 7am–3pm shift. Interested?`;
	}
	// Default: Generic pickup request
	return "Thanks for offering! We have a Tuesday 3pm–11pm shift available. Would you like it?";
};

// Format bad day at work response
const formatBadDayResponse = (): string => {
	return "I'm sorry to hear you're having a difficult day. This is important to us and we will let HR know. Is there anything specific I can help you with right now?";
};

// Format the next shift response
const formatNextShift = (shifts: Record<string, NurseInShift[]> | undefined): string => {
	const nextShiftData = findNextShift(shifts);

	if (!nextShiftData) {
		return "I couldn't find any upcoming shifts for you.";
	}

	const { shift, date } = nextShiftData;
	const startTime = shift.startTime.toFormat("ha");
	const endTime = shift.endTime.toFormat("ha");

	// Determine relative time (today, tomorrow, or specific date)
	let timeDescription: string;
	if (date.startOf("day").equals(DateTime.now().startOf("day"))) {
		timeDescription = "today";
	} else if (date.startOf("day").equals(DateTime.now().plus({ days: 1 }).startOf("day"))) {
		timeDescription = "tomorrow";
	} else {
		timeDescription = `on ${date.toFormat("cccc, MMMM D")}`;
	}

	// Include location/unit if available
	const locationPart = shift.location ? ` in the ${shift.location}` : "";

	return `Your next shift is ${timeDescription}, ${startTime}–${endTime}${locationPart}. Need anything changed?`;
};

// Format shifts for a specific week
const formatWeeklySchedule = (shifts: Record<string, NurseInShift[]> | undefined, startOfWeek: DateTime): string => {
	if (!shifts) return "I couldn't find your schedule at the moment.";

	const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
	const scheduleLines: string[] = [];

	for (let i = 0; i < 7; i++) {
		const currentDay = startOfWeek.plus({ day: i });
		const dateKey = currentDay.toFormat("yyyy-MM-dd");
		const dayShifts = shifts[dateKey] || [];

		if (dayShifts.length === 0) {
			scheduleLines.push(`${daysOfWeek[currentDay.weekday % 7]}: Off`);
		} else {
			// Assuming one shift per day, take the first shift
			const shift = dayShifts[0];
			const startTime = shift.startTime.toFormat("ha");
			const endTime = shift.endTime.toFormat("ha");
			scheduleLines.push(`${daysOfWeek[currentDay.weekday % 7]}: ${startTime}–${endTime}`);
		}
	}

	return scheduleLines.join("\n\n");
};

export const ChatBot: FC = () => {
	const { userInfo } = useAuth();
	const { track } = useAnalytics();
	const { data: shifts } = useUserShifts();
	const [messages, setMessages] = useState<Message[]>(() => DEFAULT_MESSAGES);
	const [inputMessage, setInputMessage] = useState<string>("");
	const [isTyping, setIsTyping] = useState(false);

	// Get the start of the current week (Sunday)
	const startOfWeek = useMemo(() => DateTime.now().startOf("week"), []);

	const onSendMessage = (e: FormEvent): void => {
		e.preventDefault();

		if (inputMessage.trim() === "") return;

		track("chatbot_message_send", {
			username: userInfo.username,
			message: inputMessage,
		});

		const userMessage: Message = {
			id: uuidv4(),
			text: inputMessage,
			sender: "user",
			timestamp: new Date().toISOString(),
		};

		setMessages((prevMessages) => [...prevMessages, userMessage]);

		// Process message and generate response
		const trimmedInput = inputMessage.trim();
		setInputMessage("");
		setIsTyping(true);

		// Simulate typing delay
		setTimeout(() => {
			let botResponse = "";

			// Detect intent and generate appropriate response
			// Priority: Call-out (most urgent) > Bad day at work > Pickup shift > Next shift > Weekly schedule > Fallback
			if (detectCallOutIntent(trimmedInput)) {
				const isQuestion = detectCallOutQuestionIntent(trimmedInput);
				botResponse = formatCallOutResponse(isQuestion);
			} else if (detectBadDayIntent(trimmedInput)) {
				botResponse = formatBadDayResponse();
			} else if (detectPickupShiftIntent(trimmedInput)) {
				botResponse = formatPickupShiftResponse(trimmedInput);
			} else if (detectNextShiftIntent(trimmedInput)) {
				botResponse = formatNextShift(shifts);
			} else if (detectScheduleIntent(trimmedInput)) {
				const schedule = formatWeeklySchedule(shifts, startOfWeek);
				botResponse = `Sure. Here's your schedule for the week:\n\n${schedule}\n\nLet me know if you want to swap or pick up anything.`;
			} else {
				botResponse = "I'm not sure how to help with that. You can ask me about your schedule, shifts, or when you're working.";
			}

			const botMessage: Message = {
				id: uuidv4(),
				text: botResponse,
				sender: "server",
				timestamp: new Date().toISOString(),
			};

			setMessages((prevMessages) => [...prevMessages, botMessage]);
			setIsTyping(false);
		}, 1500);
	};

	return (
		<Box
			sx={{
				position: "relative",
				display: "flex",
				flexDirection: "column",
				height: "100%",
				alignItems: "center",
				justifyContent: "center",
			}}
		>
			<Chat
				messages={messages}
				typing={isTyping}
				onSendMessage={onSendMessage}
				connected={true}
				inputMessage={inputMessage}
				setInputMessage={setInputMessage}
				userImage={userInfo.image}
			/>
		</Box>
	);
};
